import { DbRef } from '@caresend/types';
import { nullishFilter, arrayToObj, undefinedToNull, objectKeys } from '@caresend/utils';
import { getDatabase, ref, onValue, off, get, query, orderByChild, equalTo, set, update } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { handleBindErrors } from './helpers.js';
import { addSentryBreadcrumb } from '../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../components/ui/Icon.vue.js';
import '../../functions/configuration/icons.js';
import '../algoliaConfig/indices.js';
import 'mobile-device-detect';
import { analytics } from '../../functions/tracking/index.js';

/* eslint-disable @typescript-eslint/no-use-before-define */
const functions = getFunctions();
const db = getDatabase();
/**
 * Paths here will not be unbound automatically by logic such as
 * `unbindByContext`. Paths are added here by  calling `firebaseBind` with
 * option `preventAutoUnbind`. Unbinding and removal from this set may be
 * forced by calling `firebaseUnbind` with option `force`.
 */
const persistentBindingPaths = new Set();
/**
 * Firebase HTTPS Callable function wrapper that enforces request and response
 * types.
 */
const firebaseCallable = (functionName, options) => {
    const request = httpsCallable(functions, functionName, options !== null && options !== void 0 ? options : { timeout: 540000 });
    return async (payload) => {
        addSentryBreadcrumb(`Calling ${functionName}`, 'Cloud Function', payload);
        try {
            const response = await request(payload);
            addSentryBreadcrumb(`${functionName} responded`, 'Cloud Function');
            return response.data;
        }
        catch (error) {
            console.error(`Error calling function ${functionName}`, payload);
            throw error;
        }
    };
};
/**
 * For objects, the estimated size is sufficient to be represented as
 * the sum of the sizes of all keys and values.
 *
 * A key will always be a literal type (string, number, boolean, etc.) so
 * will immediately collapse.
 * A value can be any type. This includes objects, for which `sizeOf` is
 * recursively called to aggregate towards the size of the parent object.
 */
const getObjectSize = (item) => !item ? 0 : objectKeys(item)
    .reduce((total, key) => {
    const keySize = sizeOf(key);
    const valueSize = sizeOf(item[key]);
    return keySize + valueSize + total;
}, 0);
/** Estimated sizes of various JS types */
const typeSizeGetters = {
    undefined: () => 0,
    boolean: () => 4,
    number: () => 8,
    string: (item) => 2 * item.length,
    object: getObjectSize,
};
/**
 * Get the size of an object recursively, hoisted as a `function` for access to `typeSizes`
 * @param value - The value to get the size of
 * @returns The size of the value in bytes
 */
function sizeOf(value) {
    var _a, _b;
    return (_b = (_a = typeSizeGetters[typeof value]) === null || _a === void 0 ? void 0 : _a.call(typeSizeGetters, value)) !== null && _b !== void 0 ? _b : 0;
}
let sessionBytes = 0;
const getFriendlySizes = (size) => {
    const kb = Math.round((size / 1024) * 100) / 100;
    const mb = Math.round((kb / 1024) * 100) / 100;
    return {
        formatted: `${size} bytes | ${kb} KB | ${mb} MB`,
        bytes: size,
        kilobytes: kb,
        megabytes: mb,
    };
};
const dataFetchEvents = [];
const TIMEOUT_MS = 60000; // 1 minute
let batchingTimeout = null;
const flushBatch = () => {
    var _a;
    if (!dataFetchEvents.length)
        return;
    const sessionSize = getFriendlySizes(sessionBytes);
    (_a = analytics.track) === null || _a === void 0 ? void 0 : _a.call(analytics, 'firebase_data_fetch', {
        sessionSize,
        dataFetchEvents,
    });
    dataFetchEvents.length = 0;
    batchingTimeout = null;
};
const batchedTrackDataFetch = (e) => {
    dataFetchEvents.push(e);
    if (!batchingTimeout) {
        batchingTimeout = setTimeout(flushBatch, TIMEOUT_MS);
    }
};
/** Ensure all events are flushed on page unload */
window.addEventListener('beforeunload', flushBatch);
const trackDataFetch = (path, item) => {
    const size = sizeOf(item);
    sessionBytes += size;
    const [dbRef, ...rest] = path.split('/');
    if (!path || !dbRef)
        return;
    batchedTrackDataFetch({
        path: rest.join('/'),
        dbRef,
        size: getFriendlySizes(size),
    });
};
const firebaseBind = (path, callback, options, errorCallback) => new Promise((resolve, reject) => {
    const dbRef = ref(db, path);
    if (options === null || options === void 0 ? void 0 : options.preventAutoUnbind)
        persistentBindingPaths.add(path);
    getValueOnce(`${DbRef.FEATURE_FLAGS}/trackDataFetch`).then((trackDataFetchEnabled) => {
        onValue(dbRef, (snapshot) => {
            const response = snapshot.val();
            if (trackDataFetchEnabled) {
                trackDataFetch(path, response);
            }
            resolve(callback(response));
        }, (error) => {
            handleBindErrors(error.message, path);
            firebaseUnbind(path);
            if (errorCallback) {
                errorCallback();
            }
            else {
                reject(new Error(error.message));
            }
        });
    });
});
const firebaseUnbind = (path, options) => {
    if (persistentBindingPaths.has(path)) {
        if (options === null || options === void 0 ? void 0 : options.force) {
            persistentBindingPaths.delete(path);
        }
        else {
            return;
        }
    }
    const dbRef = ref(db, path);
    off(dbRef);
};
/**
 * Gets the value at a given path in firebase. If there is an error such as
 * a network connectivity or permissions issue, a toast will be displayed.
 * If `true` is passed as the second argument, the toast will be skipped and
 * an error will be thrown.
 * Otherwise it will return null. Generic type must be passed.
 */
const getValueOnce = async (path, throws = false) => {
    try {
        const snapshot = await get(ref(db, path));
        return snapshot.val();
    }
    catch (error) {
        console.info(`Failed to get path ${path}`);
        if (throws) {
            throw error;
        }
        else {
            // TODO: Currently suppressing toasts for failed data fetches as
            // we have cases in our apps where we are trying to fetch data that the
            // user no longer has access to, in which the user does not experience
            // any other issues beyond the toast. (CARE-4840)
            // toastError('There was a problem while fetching data.');
            console.error(error);
        }
        return null;
    }
};
const getValuesOnceWithFilter = async (path, key, value) => {
    var _a;
    const filteredQuery = query(ref(db, path), orderByChild(key), equalTo(value));
    const snapshot = await get(filteredQuery);
    return (_a = snapshot.val()) !== null && _a !== void 0 ? _a : {};
};
/**
 * Throws an error if the firebase client is offline. Attempts to bind will
 * wait indefinitely if the client is offline, so this check can be used if
 * we need to ensure the client is online before attempting to bind.
 */
const throwIfOffline = () => new Promise((resolve, reject) => {
    const connectedRef = ref(db, '.info/connected');
    onValue(connectedRef, (snap) => {
        if (snap.val() === true) {
            resolve();
        }
        else {
            reject();
        }
    });
});
/**
 * Gets the values at a given set of paths in firebase. Paths must be of the
 * same type (for example, all users or all places). If there are any null
 * values found, or if there is an error such as a network connectivity or
 * or permissions issue, a toast will be displayed.
 *
 * If `true` is passed as the second argument, an error will be thrown in any
 * of the cases in which a toast is shown. Otherwise it will return an array
 * containing any values that were not nullish.
 * Generic type must be passed.
 *
 * Returns `T[]`. For `ByID<T>`, use `getValuesOnceByID`.
 */
const getValuesOnce = async (paths, 
/**
 * By default, a toast will be shown if any null value is returned. Set this
 * to true to skip this toast.
 */
ignoreNulls = false, 
/**
 * Whether to throw an error in case of null values or a caught error. If
 * ignoreNulls and throws are both true, an error will only be thrown in
 * case of a caught error.
 */
throws = false, 
/**
 * Whether to suppress all toasts.
 *
 * Currently not supported as we are suppressing all toasts for failed data
 * fetches. (CARE-4840)
 */
_noToasts = false) => {
    try {
        const nullPaths = [];
        const promises = paths.map((path) => {
            const value = getValueOnce(path);
            if (value === null)
                nullPaths.push(path);
            return value;
        });
        const values = await Promise.all(promises);
        const nonNullishValues = values.filter(nullishFilter);
        if (nullPaths.length && !ignoreNulls) {
            // TODO: Currently suppressing toasts for failed data fetches as
            // we have cases in our apps where we are trying to fetch data that the
            // user no longer has access to, in which the user does not experience
            // any other issues beyond the toast. (CARE-4840)
            // if (!noToasts) toastError('There was a problem while fetching data.');
            const message = `Null values found at paths ${nullPaths.join(', ')}`;
            if (throws) {
                throw Error(message);
            }
            else {
                console.info(message);
            }
        }
        return nonNullishValues;
    }
    catch (error) {
        // TODO: Currently suppressing toasts for failed data fetches as
        // we have cases in our apps where we are trying to fetch data that the
        // user no longer has access to, in which the user does not experience
        // any other issues beyond the toast. (CARE-4840)
        // if (!noToasts) toastError('There was a problem while fetching data.');
        console.info(`Failed to get paths ${paths}`);
        if (throws)
            throw error;
        else
            console.error(error);
        return [];
    }
};
/**
 * Gets the values at a given set of paths in firebase. Paths must be of the
 * same type (for example, all users or all places). If there are any null
 * values found, or if there is an error such as a network connectivity or
 * or permissions issue, a toast will be displayed.
 *
 * If `true` is passed as the second argument, an error will be thrown in any
 * of the cases in which a toast is shown. Otherwise it will return an array
 * containing any values that were not nullish. Generic type must be passed.
 *
 * Returns `ByID<T>`. For `T[]`, use `getValuesOnceByID`.
 */
const getValuesOnceByID = async (paths, 
/**
 * By default, a toast will be shown if any null value is returned. Set this
 * to true to skip this toast.
 */
ignoreNulls = false, 
/**
 * Whether to throw an error in case of null values or a caught error. If
 * ignoreNulls and throws are both true, an error will only be thrown in
 * case of a caught error.
 */
throws = false, 
/** Whether to suppress all toasts. */
noToasts = false) => {
    const values = await getValuesOnce(paths, ignoreNulls, throws, noToasts);
    return arrayToObj(values, 'id');
};
/**
 * Set the value at a path in firebase. Existing data at that location will
 * be overwritten. Paths that do not exist yet will be created. Generic type
 * must be passed.
 *
 * Example:
 * ```
 * await dbSet<UserStatus>(getUserPath('abc123', 'status'), UserStatus.APPROVED);
 * ```
 *
 * https://firebase.google.com/docs/database/web/read-and-write#basic_write
 */
const dbSet = (path, 
// Enforce explicit type annotation with NoInfer
value) => set(ref(db, path), value);
/**
 * Update the value at a path in firebase. If `value` is an object, root-level
 * keys will be merged with existing keys. Setting a key’s value to `null` will
 * delete the key. Setting a key’s value to `undefined` will throw a runtime
 * error. Paths that do not exist yet will be created. Generic type must be
 * passed.
 *
 * This is not intended to update a single literal value, such as a string or
 * boolean. Use `dbSet()` instead.
 *
 * Example:
 * ```
 * await dbUpdate<ByID<boolean | null>>(
 *   getUserPath('abc123', 'supplies/supplyItems'),
 *   {
 *     'def456': true,
 *     'ghi789': true,
 *     'jkl123': null,
 *   }
 * );
 * ```
 *
 * https://firebase.google.com/docs/database/web/read-and-write#update_specific_fields
 */
const dbUpdate = (path, 
// Enforce explicit type annotation with NoInfer
value) => update(ref(db, path), value);
/**
 * Update the values at multiple paths in firebase. Keys in `updates` represent
 * firebase paths. Existing data at each path will be overwritten. Paths that do
 * not exist yet will be created. Generic type(s) must be passed.
 *
 * Example:
 * ```
 * await dbGroupSet<boolean | SupplyItem>({
 *   [getUserPath('abc123', 'supplies/supplyItems/def456')]: true,
 *   [getSupplyItemPath('def456')]: updatedSupplyItem,
 * });
 * ```
 *
 * https://firebase.google.com/docs/database/web/read-and-write#update_specific_fields
 * https://medium.com/@danbroadbent/b433565fd8a5
 */
const dbGroupSet = (updates) => update(ref(db), undefinedToNull(updates));

export { dbGroupSet, dbSet, dbUpdate, firebaseBind, firebaseCallable, firebaseUnbind, getValueOnce, getValuesOnce, getValuesOnceByID, getValuesOnceWithFilter, throwIfOffline };
